import Select from 'react-select';
import React, { Component } from 'react';
import './JurisdictionSelect.scss';
import * as _ from 'lodash';
import { flags } from './flags';
import {
    isSandboxBrand,
    isSandboxJurisdiction,
    JurisdictionType
} from '../models/jurisdictions';

const JurisdictionSelectOptionType = Object.freeze({
    country: 'COUNTRY',
    brand: 'BRAND',
    divider: 'DIVIDER'
});

const makeOption = ({ value, optionType, label }) => ({
    value,
    optionType,
    isDisabled: optionType === JurisdictionSelectOptionType.divider
});

const CountryCodeEnum = Object.freeze({
    MT: 'MT',
    GB: 'GB',
    SE: 'SE'
});

const CountryJurisdictionCode = Object.freeze({
    [CountryCodeEnum.MT]: 'MGA',
    [CountryCodeEnum.GB]: 'UKGC',
    [CountryCodeEnum.SE]: 'SGA'
});

// The regulator comes from the API when it gives one; these are what MT, GB and SE have been shown as, for when it does not.
const mapCountryCodeToJurisdictionCode = countryCode => {
    switch (countryCode) {
        case CountryCodeEnum.MT:
            return CountryJurisdictionCode.MT;
        case CountryCodeEnum.GB:
            return CountryJurisdictionCode.GB;
        case CountryCodeEnum.SE:
            return CountryJurisdictionCode.SE;
        case null:
            return 'All jurisdictions';
        default:
            return countryCode;
    }
};

/*
  What a jurisdiction is called in the selector: a sandbox by its name (it has no regulator), a real one by its regulator, from the
  API, or for MT, GB and SE as they have always been shown, or else by its code. "All jurisdictions" has no code.
*/
export const getJurisdictionLabel = jurisdiction => {
    if (isSandboxJurisdiction(jurisdiction)) {
        return jurisdiction.name;
    }
    const code = _.get(jurisdiction, 'code', null);

    return (
        (code !== null && jurisdiction.regulator) ||
        mapCountryCodeToJurisdictionCode(code)
    );
};

const brandJurisdictionToOpt = ({ jurisdiction, brand }) =>
    makeOption({
        value: { jurisdiction: jurisdiction, brand },
        optionType: brand
            ? JurisdictionSelectOptionType.brand
            : JurisdictionSelectOptionType.country
    });

const makeAllJurisdiction = jurisdictions => ({
    id: null,
    code: null,
    name: 'All jurisdictions'
});

const makeDivider = () =>
    makeOption({ optionType: JurisdictionSelectOptionType.divider });

export const jurisdictionsAndBrandsToViewModel = ({
    jurisdictions = [],
    brands = []
} = {}) => [
    makeDivider(),
    ..._.flatMap([makeAllJurisdiction(), ...jurisdictions], jurisdiction => [
        // Make jurisdiction option (the default, top level country only option),
        brandJurisdictionToOpt({ jurisdiction, brand: null }),
        // Right under it, make one copy per brand:
        ...brands
            .filter(
                brand =>
                    brand.jurisdiction === jurisdiction.code ||
                    // "All jurisdictions" has the brands of the real ones: a sandbox has its own, and is never part of "all"
                    (!jurisdiction.code && !isSandboxBrand(brand))
                // Convert this brand into a brandJurisdictionOpt
            )
            .map(brand =>
                brandJurisdictionToOpt({
                    jurisdiction,
                    brand
                })
            ),
        // Create the divider:
        makeDivider()
    ])
];

const svgPath = iso => flags[iso];

/* A sandbox is a copy of a jurisdiction and has no flag of its own: it says what it is, with an icon. */
const SandboxIcon = ({ className }) => (
    <span className={(className || '') + ' sandbox-icon'}>
        <i className={'material-icons'}>science</i>
    </span>
);

/* For a code that has no flag, so that no image is broken. */
const NeutralFlag = ({ className }) => (
    <span className={(className || '') + ' neutral-flag'}>
        <i className={'material-icons'}>public</i>
    </span>
);

/*
  The flag of a country. A sandbox (type SIMULATION) gets the icon of one, and a code without a flag a neutral one. Used for a
  jurisdiction, and for a country (such as in the geography of the players).
*/
export const Flag = ({ isoCode, className, type }) => {
    if (type === JurisdictionType.SIMULATION) {
        return <SandboxIcon className={className} />;
    }
    const src = svgPath((isoCode || 'all').toLowerCase());

    return src ? (
        <img {...{ className }} src={src} alt={'Flag'} />
    ) : (
        <NeutralFlag className={className} />
    );
};

/*
  TODO: Refactor. In the state, set the default jurisdiction + brand
  When props change, you either:
   1) Receive new list of jurisdictions
   2) or, receive new value
   3) setState(value: with value sent)
*/
const getSelectedJurisdiction = (
    jurisdictionsAndBrands,
    jurisdictionAndBrand
) => {
    const options = jurisdictionsAndBrandsToViewModel(jurisdictionsAndBrands);

    const firstJurisdiction = options && options[1];
    const selectedOption = options
        .filter(option => !option.isDisabled)
        .find(
            option =>
                _.get(option, 'value.jurisdiction.code') ===
                    _.get(jurisdictionAndBrand, 'jurisdiction.code') &&
                _.get(option, 'value.brand.id') ===
                    _.get(jurisdictionAndBrand, 'brand.id')
        );

    if (!selectedOption) {
        return firstJurisdiction;
    } else {
        return selectedOption;
    }
};

/* Says that what is listed is a sandbox, whatever it is called. */
const SandboxBadge = () => <span className={'sandbox-badge'}>Sandbox</span>;

const countryBrandOption = ({ value, optionType, bigIcon }) => (
    <div className={'country option' + (bigIcon ? ' big' : '')}>
        <Flag
            isoCode={value.jurisdiction.code}
            type={value.jurisdiction.type}
            className={'flag'}
        />
        <span style={{ fontSize: 14 }} className={'jurisdiction'}>
            {getJurisdictionLabel(value.jurisdiction)}
        </span>
        {(isSandboxJurisdiction(value.jurisdiction) ||
            isSandboxBrand(value.brand)) && <SandboxBadge />}
        {value.brand && (
            <span className={'brand'}> {_.get(value, 'brand.name')} </span>
        )}
    </div>
);

const brandOption = ({ value, optionType }) => (
    <div className={'brand option'}>
        <span style={{ fontSize: 14 }}>{value.brand.name}</span>
        {isSandboxBrand(value.brand) && <SandboxBadge />}
    </div>
);

const dividerOption = (
    <span className={'divider option'} style={{ height: '50px' }} />
);

const getJurisdictionControl = optionArgs => {
    switch (optionArgs.optionType) {
        case JurisdictionSelectOptionType.country:
            return countryBrandOption(optionArgs);
        case JurisdictionSelectOptionType.brand:
            return brandOption(optionArgs);
        case JurisdictionSelectOptionType.divider:
            return dividerOption;
        default:
            return;
    }
};

const getBackgroundColor = menuIsOpen => (menuIsOpen ? '#FFF' : 'transparent');

const selectStyles = ({ height, menuIsOpen }) => ({
    indicatorSeparator: provided => ({
        display: 'none'
    }),
    indicatorsContainer: provided => ({
        ...provided,
        paddingRight: '17px'
    }),
    option: provided => ({
        ...provided,
        color: ' inherit',
        padding: '0px 0px',
        backgroundColor: '#FFF',

        '&:hover': {
            backgroundColor: '#FFF'
        }
    }),
    menu: provided => ({
        ...provided,
        borderRadius: '0px',
        marginTop: '0px',
        boxShadow: '0px 12px 25px 2px rgba(0,0,0,0.1)',
        clipPath: 'inset(0px -40px -40px -40px)',
        transition: 'all 200ms'
    }),
    menuList: provided => ({
        ...provided,
        maxHeight: 'calc(100vh - 200px)'
    }),
    control: provided => ({
        ...provided,
        height: height + 'px',
        borderBottom: 'none',
        borderRadius: 0,
        border: 'none',
        backgroundColor: getBackgroundColor(menuIsOpen),
        boxShadow: menuIsOpen ? '0px 12px 25px 2px rgba(0,0,0,0.1)' : 'none',
        transition: 'all 200ms'
    }),
    valueContainer: provided => ({
        ...provided,
        minHeight: '1px',
        height: height + 'px',
        padding: '12.5px 0px',
        zIndex: '999'
    })
});

export class JurisdictionSelect extends Component {
    constructor() {
        super();
        this.state = {
            menuIsOpen: false
        };
    }

    setMenuOpen(menuIsOpen) {
        this.setState({
            menuIsOpen
        });
    }

    render = () => {
        const {
            jurisdictionsAndBrands,
            onJurisdictionChanged,
            jurisdiction
        } = this.props;
        const { menuIsOpen } = this.state;

        return (
            <Select
                onMenuOpen={() => this.setMenuOpen(true)}
                onMenuClose={() => this.setMenuOpen(false)}
                className={'jurisdiction-select'}
                onChange={change => onJurisdictionChanged(change.value)}
                value={getSelectedJurisdiction(
                    jurisdictionsAndBrands,
                    jurisdiction
                )}
                components={{
                    SingleValue: ({ data }) =>
                        countryBrandOption({ ...data, bigIcon: true })
                }}
                formatOptionLabel={getJurisdictionControl}
                placeholder={''}
                isSearchable={false}
                styles={selectStyles({
                    height: 100,
                    menuIsOpen
                })}
                options={jurisdictionsAndBrandsToViewModel(
                    jurisdictionsAndBrands
                )}
            />
        );
    };
}
